import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, Search, ArrowLeft, Mail } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { buscarClientePorCpf } from '../services/api';

const CheckDocument = () => {
  const [document, setDocument] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value;
    // Se contém apenas números, formata como CPF
    if (/^\d+$/.test(value.replace(/\D/g, ''))) {
      setDocument(formatCPF(value));
    } else {
      setDocument(value);
    }
    setError('');
  };

  const validateInput = () => {
    if (!document.trim()) {
      setError('Por favor, informe seu CPF ou e-mail');
      return false;
    }

    // Verificar se é email
    if (document.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(document)) {
        setError('Por favor, informe um e-mail válido');
        return false;
      }
    } else {
      // Verificar se é CPF válido
      const cpfNumbers = document.replace(/\D/g, '');
      if (cpfNumbers.length !== 11) {
        setError('Por favor, informe um CPF válido');
        return false;
      }
    }

    return true;
  };

  const handleCheckDocument = async () => {
    if (!validateInput()) return;

    setLoading(true);
    setError('');

    try {
      // Buscar cliente pelo CPF na API
      const cliente = await buscarClientePorCpf(document);
      
      toast({
        title: "Usuário encontrado!",
        description: `${cliente.nome} - Redirecionando para alteração de senha...`,
        duration: 3000,
      });

      // Navegar para página de alteração de senha passando dados do cliente
      navigate('/change-password', { 
        state: { 
          userId: cliente.id,
          document: document,
          nome: cliente.nome
        } 
      });

    } catch (err) {
      setError(err.message || 'Usuário não encontrado. Verifique os dados informados.');
      toast({
        title: "Erro na verificação",
        description: err.message || "Usuário não encontrado. Verifique os dados informados.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo e título */}
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_knorr-auction-proxy/artifacts/ud6gmi7h_Screenshot_7-removebg-preview.png"
            alt="Knorr Leilões"
            className="h-16 w-auto mx-auto mb-4"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <h1 className="text-4xl font-bold text-[rgb(46,48,146)] mb-2 hidden">
            KNORR LEILÕES
          </h1>
          <p className="text-gray-600">Recuperação de Senha</p>
        </div>

        <Card className="border-2 border-gray-200">
          <CardHeader className="text-center bg-[rgb(46,48,146)] text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>Verificar Cadastro</span>
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6">
            <div className="space-y-6">
              <div className="text-center text-sm text-gray-600 mb-4">
                Informe seu CPF ou e-mail para verificarmos se você possui cadastro
              </div>

              <div>
                <Label htmlFor="document" className="text-sm font-medium text-gray-700">
                  CPF ou E-mail
                </Label>
                <Input
                  id="document"
                  type="text"
                  placeholder="000.000.000-00 ou email@exemplo.com"
                  value={document}
                  onChange={handleDocumentChange}
                  className="mt-1 border-gray-300 focus:border-[rgb(46,48,146)] focus:ring-[rgb(46,48,146)]"
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Para CPF, adicione pontuações e caracteres especiais
                </p>
              </div>

              {error && (
                <Alert className="border-[rgb(238,29,37)] text-[rgb(238,29,37)]">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handleCheckDocument}
                disabled={loading}
                className="w-full bg-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/90 text-white py-3 transition-colors duration-200"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Verificando...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Search className="h-4 w-4" />
                    <span>Verificar Login</span>
                  </div>
                )}
              </Button>

              <div className="text-center">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-[rgb(46,48,146)] hover:bg-[rgb(46,48,146)]/10"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar ao Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Problemas? Entre em contato conosco através do WhatsApp
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckDocument;